import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Save, User, MapPin, Briefcase, DollarSign, 
  Calendar, Users, FileText, AlertCircle, Search, UserPlus, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import debounce from 'lodash/debounce';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const estimateId = searchParams.get('estimate_id');
  const contactIdFromUrl = searchParams.get('contact_id');
  
  const [saving, setSaving] = useState(false);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  
  // Contact selection state
  const [contactMode, setContactMode] = useState('auto'); // 'auto' or 'existing'
  const [contactSearch, setContactSearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactDropdown, setShowContactDropdown] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    // IDs
    contact_id: contactIdFromUrl || '',
    estimate_id: estimateId || '',
    
    // Customer info
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    
    // Job details
    job_name: '',
    job_type: 'RhinoShield Exterior',
    status: 'lead',
    priority: 'medium',
    
    // Scope
    scope_details: '',
    special_instructions: '',
    
    // Financial
    contract_amount: '',
    square_footage: '',
    
    // Assignment
    assigned_crew: '',
    
    // Schedule
    scheduled_start: '',
    scheduled_end: ''
  });

  const [errors, setErrors] = useState({});

  // Job field options (fetched from API)
  const [jobFieldOptions, setJobFieldOptions] = useState({
    job_type: [
      'RhinoShield Exterior',
      'RhinoShield Roof',
      'Deck Seal',
      'Interior Painting',
      'Pressure Washing',
      'Gutter Installation',
      'Window Replacement',
      'Other'
    ],
    project_status: ['Lead', 'Sold', 'In Progress', 'Scheduled', 'Complete', 'On Hold', 'Cancelled']
  });

  // Crews list
  const [crews, setCrews] = useState([]);

  // Fetch job field options
  useEffect(() => {
    const fetchFieldOptions = async () => {
      try {
        const response = await axios.get(`${API}/settings/job-field-options`);
        if (response.data) {
          setJobFieldOptions(prev => ({
            ...prev,
            job_type: response.data.job_type || prev.job_type,
            project_status: response.data.project_status || prev.project_status
          }));
        }
      } catch (error) {
        console.log('Using default field options');
      }
    };
    fetchFieldOptions();
  }, []);

  // Fetch crews (teams with department "Crews")
  useEffect(() => {
    const fetchCrews = async () => {
      try {
        const response = await axios.get(`${API}/teams`);
        // Filter teams that are crews (department contains "Crew")
        const crewTeams = (response.data || []).filter(team => 
          team.department?.toLowerCase().includes('crew')
        );
        setCrews(crewTeams);
      } catch (error) {
        console.error('Error fetching crews:', error);
      }
    };
    fetchCrews();
  }, []);

  // Search contacts with debounce
  const searchContacts = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setContacts([]);
        return;
      }
      
      setLoadingContacts(true);
      try {
        const response = await axios.get(`${API}/contacts`, {
          params: { search: query, per_page: 10 }
        });
        setContacts(response.data.contacts || response.data || []);
      } catch (error) {
        console.error('Error searching contacts:', error);
        setContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    }, 300),
    []
  );

  // Load contact if contact_id provided in URL
  useEffect(() => {
    const loadContactFromUrl = async () => {
      if (contactIdFromUrl) {
        try {
          const response = await axios.get(`${API}/contacts/${contactIdFromUrl}`);
          const contact = response.data;
          setSelectedContact(contact);
          setContactMode('existing');
          // Pre-fill form with contact data
          setFormData(prev => ({
            ...prev,
            contact_id: contactIdFromUrl,
            customer_name: `${contact.first_name} ${contact.last_name}`.trim(),
            customer_email: contact.email || '',
            customer_phone: contact.phone || '',
            address: contact.address || '',
            city: contact.city || '',
            state: contact.state || '',
            zip_code: contact.zip_code || ''
          }));
        } catch (error) {
          console.error('Error loading contact:', error);
          toast.error('Could not load contact');
        }
      }
    };
    loadContactFromUrl();
  }, [contactIdFromUrl]);

  // Handle contact search input
  useEffect(() => {
    if (contactMode === 'existing' && contactSearch) {
      searchContacts(contactSearch);
    }
  }, [contactSearch, contactMode, searchContacts]);

  // Select an existing contact
  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setFormData(prev => ({
      ...prev,
      contact_id: contact.id,
      customer_name: `${contact.first_name} ${contact.last_name}`.trim(),
      customer_email: contact.email || '',
      customer_phone: contact.phone || '',
      address: contact.address || prev.address,
      city: contact.city || prev.city,
      state: contact.state || prev.state,
      zip_code: contact.zip_code || prev.zip_code
    }));
    setShowContactDropdown(false);
    setContactSearch('');
  };

  // Clear selected contact
  const handleClearContact = () => {
    setSelectedContact(null);
    setFormData(prev => ({
      ...prev,
      contact_id: '',
      customer_name: '',
      customer_email: '',
      customer_phone: ''
    }));
  };

  useEffect(() => {
    if (estimateId) {
      loadEstimateData();
    }
  }, [estimateId]);

  const loadEstimateData = async () => {
    setLoadingEstimate(true);
    try {
      // Try to fetch estimate data if endpoint exists
      const response = await axios.get(`${API}/estimates/${estimateId}`);
      const estimate = response.data;
      
      // Pre-fill form with estimate data
      setFormData(prev => ({
        ...prev,
        customer_name: estimate.customer_name || '',
        customer_email: estimate.customer_email || '',
        customer_phone: estimate.customer_phone || '',
        address: estimate.address || '',
        city: estimate.city || '',
        state: estimate.state || '',
        zip_code: estimate.zip_code || '',
        job_type: estimate.project_type || prev.job_type,
        contract_amount: estimate.total_amount || '',
        square_footage: estimate.square_footage || '',
        scope_details: estimate.scope || ''
      }));
      
      toast.success('Estimate data loaded');
    } catch (error) {
      console.log('Could not load estimate data (endpoint may not exist yet)');
      // Not a critical error - just means we're creating from scratch
    } finally {
      setLoadingEstimate(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }
    
    if (!formData.customer_phone.trim()) {
      newErrors.customer_phone = 'Phone number is required';
    }
    
    if (!formData.job_type) {
      newErrors.job_type = 'Job type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      // Prepare data - convert empty strings to null for optional fields
      // Set stage to match status for board compatibility
      const jobData = {
        ...formData,
        stage: formData.status, // Sync stage with status for board display
        customer_email: formData.customer_email?.trim() || null,
        contract_amount: formData.contract_amount ? parseFloat(formData.contract_amount) : null,
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        sold_at: new Date().toISOString(),
        status_changed_at: new Date().toISOString()
      };
      
      const response = await axios.post(`${API}/jobs`, jobData);
      
      toast.success('Job created successfully!');
      navigate(`/jobs/${response.data.id}`);
    } catch (error) {
      console.error('Error creating job:', error);
      // Handle different error formats
      let errorMessage = 'Failed to create job';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (loadingEstimate) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading estimate data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Create New Job</h1>
              <p className="text-sm text-muted-foreground">
                {estimateId ? 'Converting from estimate' : 'Add a new project to the system'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Selection */}
          <Card className="border-2 border-primary/20 bg-primary/5 dark:bg-primary/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                Contact Link
              </CardTitle>
              <CardDescription>
                Every job must be linked to a contact. Select an existing contact or let us create one automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mode Selection */}
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant={contactMode === 'auto' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => {
                    setContactMode('auto');
                    setSelectedContact(null);
                    setFormData(prev => ({ ...prev, contact_id: '' }));
                  }}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create New Contact
                </Button>
                <Button
                  type="button"
                  variant={contactMode === 'existing' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setContactMode('existing')}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Link Existing Contact
                </Button>
              </div>

              {/* Contact Mode: Existing - Search & Select */}
              {contactMode === 'existing' && (
                <div className="space-y-3">
                  {selectedContact ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold">
                          {selectedContact.first_name?.[0]}{selectedContact.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium">{selectedContact.first_name} {selectedContact.last_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedContact.email || selectedContact.phone || 'No contact info'}
                          </p>
                        </div>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={handleClearContact}>
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search contacts by name, email, or phone..."
                        value={contactSearch}
                        onChange={(e) => {
                          setContactSearch(e.target.value);
                          setShowContactDropdown(true);
                        }}
                        onFocus={() => setShowContactDropdown(true)}
                        className="pl-9"
                      />
                      {showContactDropdown && contactSearch.length >= 2 && (
                        <div className="absolute z-10 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-60 overflow-auto">
                          {loadingContacts ? (
                            <div className="p-4 text-center text-muted-foreground">
                              Searching...
                            </div>
                          ) : contacts.length > 0 ? (
                            contacts.map(contact => (
                              <div
                                key={contact.id}
                                className="p-3 hover:bg-muted cursor-pointer flex items-center gap-3"
                                onClick={() => handleSelectContact(contact)}
                              >
                                <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium">
                                  {contact.first_name?.[0]}{contact.last_name?.[0]}
                                </div>
                                <div>
                                  <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {contact.email || contact.phone || 'No contact info'}
                                    {contact.jobs_linked > 0 && ` • ${contact.jobs_linked} jobs linked`}
                                  </p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-muted-foreground">
                              No contacts found. Try a different search or create a new contact.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Contact Mode: Auto - Info Message */}
              {contactMode === 'auto' && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Auto-create:</strong> A new contact will be automatically created using the customer information you provide below.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Customer Information
              </CardTitle>
              <CardDescription>
                {contactMode === 'existing' && selectedContact 
                  ? 'Pre-filled from selected contact. You can edit if needed.' 
                  : 'This information will be used to create the contact record.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>
                    Customer Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="John Smith"
                    value={formData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    className={errors.customer_name ? 'border-red-500' : ''}
                  />
                  {errors.customer_name && (
                    <p className="text-sm text-red-500 mt-1">{errors.customer_name}</p>
                  )}
                </div>
                
                <div>
                  <Label>
                    Phone Number (US) <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 text-sm text-slate-500 bg-slate-100 dark:bg-slate-700 border border-r-0 border-slate-200 dark:border-slate-600 rounded-l-md">
                      +1
                    </span>
                    <Input
                      placeholder="(555) 123-4567"
                      value={formData.customer_phone?.replace(/^\+1/, '')}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleChange('customer_phone', digits);
                      }}
                      className={`rounded-l-none ${errors.customer_phone ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.customer_phone && (
                    <p className="text-sm text-red-500 mt-1">{errors.customer_phone}</p>
                  )}
                </div>
                
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.customer_email}
                    onChange={(e) => handleChange('customer_email', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Property Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Property Location
              </CardTitle>
              <CardDescription>Job site address</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Street Address</Label>
                <Input
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    placeholder="Tampa"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label>State</Label>
                  <Select value={formData.state} onValueChange={(val) => handleChange('state', val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FL">Florida</SelectItem>
                      <SelectItem value="GA">Georgia</SelectItem>
                      <SelectItem value="TX">Texas</SelectItem>
                      <SelectItem value="CA">California</SelectItem>
                      {/* Add more states as needed */}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>ZIP Code</Label>
                  <Input
                    placeholder="33606"
                    value={formData.zip_code}
                    onChange={(e) => handleChange('zip_code', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Job Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Job Details
              </CardTitle>
              <CardDescription>Project information and scope</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Job Nickname (Optional)</Label>
                  <Input
                    placeholder="e.g., Smith Exterior Coating"
                    value={formData.job_name}
                    onChange={(e) => handleChange('job_name', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    A friendly name to identify this job
                  </p>
                </div>
                
                <div>
                  <Label>
                    Job Type <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={formData.job_type} 
                    onValueChange={(val) => handleChange('job_type', val)}
                  >
                    <SelectTrigger className={errors.job_type ? 'border-red-500' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {jobFieldOptions.job_type.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.job_type && (
                    <p className="text-sm text-red-500 mt-1">{errors.job_type}</p>
                  )}
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(val) => handleChange('priority', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Initial Status</Label>
                  <Select value={formData.status} onValueChange={(val) => handleChange('status', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {jobFieldOptions.project_status.map(status => (
                        <SelectItem key={status} value={status.toLowerCase().replace(/\s+/g, '_')}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Scope Details</Label>
                <Textarea
                  rows={4}
                  placeholder="Describe the project scope, surfaces to be treated, special requirements..."
                  value={formData.scope_details}
                  onChange={(e) => handleChange('scope_details', e.target.value)}
                />
              </div>
              
              <div>
                <Label>Special Instructions</Label>
                <Textarea
                  rows={3}
                  placeholder="Any special notes, access instructions, customer preferences..."
                  value={formData.special_instructions}
                  onChange={(e) => handleChange('special_instructions', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Financial & Project Size */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Financial & Project Size
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contract Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      type="number"
                      placeholder="15000"
                      value={formData.contract_amount}
                      onChange={(e) => handleChange('contract_amount', e.target.value)}
                      className="pl-7"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Square Footage</Label>
                  <Input
                    type="number"
                    placeholder="2500"
                    value={formData.square_footage}
                    onChange={(e) => handleChange('square_footage', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scheduling */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Scheduling (Optional)
              </CardTitle>
              <CardDescription>Set initial schedule dates if known</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Scheduled Start</Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_start}
                    onChange={(e) => handleChange('scheduled_start', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label>Scheduled End</Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_end}
                    onChange={(e) => handleChange('scheduled_end', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Crew Assignment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Crew Assignment (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label>Assigned Crew</Label>
                <Select
                  value={formData.assigned_crew || 'none'}
                  onValueChange={(value) => handleChange('assigned_crew', value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a crew..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Crew Assigned --</SelectItem>
                    {crews.map(crew => (
                      <SelectItem key={crew.id} value={crew.name}>
                        {crew.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  You can assign a crew now or later from the job detail page
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/jobs')}
              disabled={saving}
            >
              Cancel
            </Button>
            
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Job...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Job
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default JobCreate;
